import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import {
  FaArrowLeft,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
} from "react-icons/fa";
import { getSeoAnalysisRunsApi } from "../../apis/projects.api";

const SeoHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const { projectId, projectName, domainUrl } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    if (!projectId) {
      setError("No project selected");
      setLoading(false);
      return;
    }
    fetchHistory();
  }, [projectId]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSeoAnalysisRunsApi(projectId);

      if (response?.data) {
        const historyData = response.data.runs || response.data || [];
        setRuns(Array.isArray(historyData) ? historyData : []);
      } else {
        setRuns([]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching SEO history:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load SEO analysis history",
      );
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return <FaCheckCircle className="text-green-500" />;
      case "failed":
      case "error":
        return <FaTimesCircle className="text-red-500" />;
      case "pending":
      case "processing":
      case "running":
        return <FaSpinner className="text-blue-500 animate-spin" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return isDarkMode
          ? "bg-green-900/30 text-green-400"
          : "bg-green-100 text-green-800";
      case "failed":
      case "error":
        return isDarkMode
          ? "bg-red-900/30 text-red-400"
          : "bg-red-100 text-red-800";
      case "pending":
      case "processing":
      case "running":
        return isDarkMode
          ? "bg-blue-900/30 text-blue-400"
          : "bg-blue-100 text-blue-800";
      default:
        return isDarkMode
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-100 text-gray-800";
    }
  };

  const handleViewResults = (run) => {
    // Navigate to the SEO results page with the correct route and run_id
    navigate(
      `/dashboard/projects/${projectId}/seo-results?run_id=${run.run_id || run.id}`,
      {
        state: {
          projectName,
          domainUrl,
        },
      },
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              Loading SEO analysis history...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate("/dashboard")}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
            >
              <FaArrowLeft
                className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              SEO Analysis History
            </h1>
          </div>

          <div
            className={`p-8 rounded-lg shadow-lg text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="text-6xl mb-4">❌</div>
            <h2
              className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Error Loading History
            </h2>
            <p
              className={`text-lg mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {error}
            </p>
            <button
              onClick={fetchHistory}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate("/dashboard")}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
            >
              <FaArrowLeft
                className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              />
            </button>
            <div className="flex-1">
              <h1
                className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                SEO Analysis History
              </h1>
              {projectName && (
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Project: {projectName}
                </p>
              )}
              {domainUrl && (
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                >
                  {domainUrl}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* History List */}
        {runs.length === 0 ? (
          <div
            className={`p-8 rounded-lg shadow-lg text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="text-6xl mb-4">📊</div>
            <h2
              className={`text-2xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              No Analysis History
            </h2>
            <p
              className={`text-lg mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              No SEO analysis runs found for this project yet.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {runs.map((run, index) => (
              <div
                key={run.run_id || run.id || index}
                className={`p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow ${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusIcon(run.page_level_status || run.status)}
                      <h3
                        className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {run.created_at
                          ? new Date(run.created_at).toLocaleString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : run.started_at
                            ? new Date(run.started_at).toLocaleString()
                            : "N/A"}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(run.page_level_status || run.status)}`}
                      >
                        {run.page_level_status || run.status || "Unknown"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div
                        className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        <FaClock />
                        <span>
                          Created:{" "}
                          {run.created_at
                            ? new Date(run.created_at).toLocaleString()
                            : run.started_at
                              ? new Date(run.started_at).toLocaleString()
                              : "N/A"}
                        </span>
                      </div>
                      {run.completed_at && (
                        <div
                          className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          <FaCheckCircle />
                          <span>
                            Completed:{" "}
                            {new Date(run.completed_at).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {run.pages_analyzed !== undefined && (
                        <div
                          className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          📄 <span>Pages Analyzed: {run.pages_analyzed}</span>
                        </div>
                      )}
                      {run.score !== undefined && (
                        <div
                          className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          ⭐ <span>Overall Score: {run.score}%</span>
                        </div>
                      )}
                    </div>

                    {run.error_message && (
                      <div
                        className={`p-3 rounded mb-3 text-sm ${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-50 text-red-800"}`}
                      >
                        Error: {run.error_message}
                      </div>
                    )}
                  </div>

                  <div className="ml-4">
                    {(run.status?.toLowerCase() === "completed" ||
                      run.status?.toLowerCase() === "success" ||
                      run.status?.toLowerCase() === "accepted" ||
                      run.status?.toLowerCase() === "ok" ||
                      run.run_id ||
                      run.id) && (
                      <button
                        onClick={() => handleViewResults(run)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
                      >
                        View Results
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeoHistory;
